import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import DOMPurify from 'dompurify'
import { Heart, Share2, Check, MessageCircle, Eye, Calendar, Clock } from 'lucide-react'
import { NotFoundScreen } from '@/components/ui/NotFoundScreen'
import { CommentSection } from '@/components/blog/CommentSection'
import { Breadcrumb } from '@/components/ui/Breadcrumb'
import { useAuthStore } from '@/stores/authStore'
import { postsService, Post } from '@/services/postsService'

const safeParseJSON = <T,>(raw: string | null, fallback: T): T => {
  if (!raw) return fallback
  try {
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

export function BlogPostScreen() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [post, setPost] = useState<Post | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [liked, setLiked] = useState(false)
  const [copied, setCopied] = useState(false)
  const [commentCount, setCommentCount] = useState(0)

  useEffect(() => {
    const loadPost = async () => {
      if (!slug) return

      try {
        setLoading(true)
        setError(null)

        const postData = await postsService.getPostBySlug(slug)
        setPost(postData)
        setCommentCount(postData.comment_count ?? 0)

        // Controle de visualizações únicas usando localStorage
        const viewedPosts = safeParseJSON<Record<string, number>>(
          localStorage.getItem('viewedPosts'),
          {}
        )
        const lastViewed = viewedPosts[postData.id]
        const now = Date.now()
        const ONE_HOUR = 60 * 60 * 1000 // 1 hora em ms

        // Só conta como visualização se nunca viu ou se passou mais de 1 hora
        if (!lastViewed || (now - lastViewed) > ONE_HOUR) {
          viewedPosts[postData.id] = now
          localStorage.setItem('viewedPosts', JSON.stringify(viewedPosts))
          // Incrementar visualização no backend
          postsService.incrementView(postData.id).catch(() => { })
        }

        if (user) {
          try {
            const likedData = await postsService.checkLiked(postData.id)
            setLiked(likedData.liked)
          } catch (err) {
            console.error('Error checking liked status:', err)
            setLiked(false)
          }
        } else {
          setLiked(false)
        }
      } catch (err) {
        console.error('Error loading post:', err)
        setError('Erro ao carregar post')
      } finally {
        setLoading(false)
      }
    }

    loadPost()
  }, [slug, user])

  const sanitizedContent = useMemo(
    () => (post?.content ? DOMPurify.sanitize(post.content) : ''),
    [post?.content]
  )

  const handleLike = async () => {
    if (!post) return

    if (!user) {
      alert('Você precisa estar logado para curtir posts')
      navigate('/login')
      return
    }

    try {
      if (liked) {
        await postsService.unlikePost(post.id)
      } else {
        await postsService.likePost(post.id)
      }

      // Recarregar post para pegar contador atualizado do backend
      const postData = await postsService.getPostBySlug(slug!)
      setPost(postData)

      // Recarregar estado de liked
      const likedData = await postsService.checkLiked(postData.id)
      setLiked(likedData.liked)
    } catch (err) {
      console.error('Error toggling like:', err)
      alert('Erro ao curtir post')
    }
  }

  const handleShare = async () => {
    const url = window.location.href
    if (navigator.share) {
      try {
        await navigator.share({ title: post?.title, text: post?.summary || '', url })
      } catch {
        // User cancelled
      }
    } else {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-brand-bg">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-brand-primary border-t-transparent"></div>
      </div>
    )
  }

  if (error || !post) {
    return <NotFoundScreen type="post" />
  }

  return (
    <div className="min-h-screen bg-brand-bg pb-20 lg:pb-0">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Breadcrumb
          items={[
            { label: 'Blog', href: '/blog' },
            { label: post.title }
          ]}
        />

        <article className="card overflow-hidden overflow-x-hidden">
          {post.cover_image_url && (
            <div className="aspect-video w-full overflow-hidden">
              <img
                src={post.cover_image_url}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-6 md:p-8">
            <div className="flex flex-wrap items-center gap-3 text-sm text-brand-text-muted mb-4">
              {post.taxonomies && post.taxonomies.length > 0 && (
                <span className="px-3 py-1 bg-brand-primary-light text-brand-primary rounded-lg font-medium text-xs">
                  {post.taxonomies[0].name}
                </span>
              )}
              <div className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(post.published_at || post.created_at)}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                <span>5 min de leitura</span>
              </div>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-brand-text mb-4">{post.title}</h1>

            {post.summary && (
              <p className="text-lg text-brand-text-secondary mb-8 leading-relaxed">{post.summary}</p>
            )}

            <div className="flex items-center gap-6 py-4 border-y border-brand-border mb-8">
              <button
                onClick={handleLike}
                className={`flex items-center gap-2 transition-colors ${liked ? 'text-red-500' : 'text-brand-text-muted hover:text-red-500'
                  }`}
              >
                <Heart className={`w-5 h-5 ${liked ? 'fill-current' : ''}`} />
                <span className="font-medium text-sm">{post.like_count}</span>
              </button>

              <div className="flex items-center gap-2 text-brand-text-muted">
                <MessageCircle className="w-5 h-5" />
                <span className="font-medium text-sm">{commentCount}</span>
              </div>

              <div className="flex items-center gap-2 text-brand-text-muted">
                <Eye className="w-5 h-5" />
                <span className="font-medium text-sm">{post.view_count}</span>
              </div>

              <button
                onClick={handleShare}
                className="ml-auto flex items-center gap-2 text-brand-text-muted hover:text-brand-primary transition-colors"
              >
                {copied
                  ? <Check className="w-5 h-5 text-green-500" />
                  : <Share2 className="w-5 h-5" />}
                <span className="font-medium text-sm">{copied ? 'Copiado!' : 'Compartilhar'}</span>
              </button>
            </div>

            <div
              className="prose prose-lg max-w-none prose-headings:text-brand-text prose-p:text-brand-text-secondary prose-a:text-brand-primary prose-strong:text-brand-text prose-blockquote:border-brand-accent prose-blockquote:bg-brand-primary-light prose-blockquote:py-2"
              dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            />

            {post.taxonomies && post.taxonomies.length > 0 && (
              <div className="mt-8 pt-8 border-t border-brand-border">
                <h3 className="text-sm font-semibold text-brand-text mb-3">Tags:</h3>
                <div className="flex flex-wrap gap-2">
                  {post.taxonomies.map((tag) => (
                    <span
                      key={tag.id}
                      className="px-3 py-1 bg-brand-surface-alt text-brand-text-secondary rounded-lg text-sm hover:bg-brand-primary-light hover:text-brand-primary transition-colors cursor-pointer"
                    >
                      #{tag.slug}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <CommentSection
              postId={post.id}
              onCommentCountChange={delta => setCommentCount(c => c + delta)}
            />
          </div>
        </article>
      </div>
    </div>
  )
}
